import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";

import CircularProgress from "@material-ui/core/CircularProgress";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Divider from "@material-ui/core/Divider";
import Typography from "@material-ui/core/Typography";

import { formatRegionLabel } from "./matchNightTime";
import { getServerLocationLabel } from "./matchNightOptions";

const basePath =
  process.env.NODE_ENV === "production" ? "/edge" : "http://localhost:3010";

const dialogPaperStyle = { backgroundColor: "#1e2023", color: "#fff", minWidth: 480 };

const STATUS_GROUPS = [
  { key: "confirmed", label: "Confirmed" },
  { key: "pending_verification", label: "Pending verification" },
  { key: "waitlisted", label: "Waitlisted" },
  { key: "checked_in", label: "Checked in" },
  { key: "no_show", label: "No show" },
  { key: "cancelled", label: "Cancelled" },
];

function relativeTime(dateStr) {
  if (!dateStr) return "";
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 2) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export default function MatchNightRosterDialog({ open, nightId, user, onClose }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !nightId || !user?.userId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetch(`${basePath}/api/v7/admin/match-nights/${nightId}/roster/adpass/${user.userId}`, {
      credentials: "include",
    })
      .then((resp) => resp.json())
      .then((json) => {
        if (cancelled) return;
        if (json.ok) {
          setData(json);
        } else {
          setError(json.message || "Failed to load roster.");
        }
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load roster.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, nightId, user]);

  const night = data?.night;

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ style: dialogPaperStyle }} maxWidth="sm" fullWidth>
      <DialogTitle style={{ color: "#fff" }}>
        {night ? `Roster, ${formatRegionLabel(new Date(night.scheduledAt), night.region)}` : "Roster"}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress style={{ color: "#7C3AED" }} />
          </Box>
        )}

        {!loading && error && (
          <Typography style={{ color: "#f44336" }}>{error}</Typography>
        )}

        {!loading && !error && night && (
          <>
            <Typography style={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.6)", marginBottom: 16 }}>
              {night.region}, {night.serverLocations.map(getServerLocationLabel).join(", ")},{" "}
              {night.counts.confirmed} confirmed of {night.minToFire} to {night.maxCapacity}
            </Typography>

            {STATUS_GROUPS.map((group) => {
              const entries = data.roster?.[group.key] || [];
              if (entries.length === 0) return null;
              return (
                <Box key={group.key} mb={2}>
                  <Typography
                    style={{
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      letterSpacing: "0.06em",
                      textTransform: "uppercase",
                      color: "rgba(255,255,255,0.5)",
                      marginBottom: 8,
                    }}
                  >
                    {group.label} ({entries.length})
                  </Typography>
                  <Divider style={{ backgroundColor: "rgba(255,255,255,0.08)", marginBottom: 8 }} />
                  {entries.map((entry) => (
                    <Box
                      key={entry.id}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      py={0.75}
                    >
                      <Box>
                        <Typography style={{ color: "#fff", fontSize: "0.9rem" }}>
                          {entry.nickname || entry.userId}
                        </Typography>
                        {entry.email && (
                          <Typography style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem" }}>
                            {entry.email}
                          </Typography>
                        )}
                      </Box>
                      <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                        {entry.verificationMethodAtRsvp && (
                          <Chip
                            label={entry.verificationMethodAtRsvp}
                            size="small"
                            style={{ backgroundColor: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.7)" }}
                          />
                        )}
                        <Typography style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.75rem" }}>
                          {relativeTime(entry.createdAt)}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Box>
              );
            })}

            {STATUS_GROUPS.every((group) => (data.roster?.[group.key] || []).length === 0) && (
              <Typography style={{ color: "rgba(255,255,255,0.45)" }}>
                No reservations yet.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions style={{ padding: "12px 24px" }}>
        <Button onClick={onClose} style={{ color: "rgba(255,255,255,0.6)" }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}

MatchNightRosterDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  nightId: PropTypes.string,
  user: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

MatchNightRosterDialog.defaultProps = {
  nightId: null,
  user: null,
};
